import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FileText, Ticket, LogOut, Shield, ShoppingCart, Settings, Activity, TrendingUp, Zap, Key } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [stats, setStats] = useState({ invoices: 0, tickets: 0, pendingInvoices: 0, openTickets: 0 });
  const [dashboardMetrics, setDashboardMetrics] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        const me = response.data;
        // Redirect staff away from the client dashboard.
        if (me.role === 'admin' || me.role === 'accountant') {
          navigate('/admin', { replace: true, state: { user: me } });
          return;
        }
        if (me.role === 'technician') {
          navigate('/tech', { replace: true, state: { user: me } });
          return;
        }
        setUser(me);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  // Catch the case where Dashboard was opened with state.user already set but
  // that user is staff — redirect immediately rather than render the client UI.
  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin' || user.role === 'accountant') navigate('/admin', { replace: true, state: { user } });
    else if (user.role === 'technician') navigate('/tech', { replace: true, state: { user } });
  }, [user, navigate]);

  useEffect(() => {
    if (user) fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const [invoicesRes, ticketsRes, metricsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoices`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tickets`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/dashboard/metrics`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      const invoices = invoicesRes.data;
      const tickets = ticketsRes.data;
      setStats({
        invoices: invoices.length,
        tickets: tickets.length,
        pendingInvoices: invoices.filter(inv => inv.status === 'pending').length,
        openTickets: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length
      });
      setDashboardMetrics(metricsRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <div className="font-black text-lg sm:text-xl uppercase tracking-tight">
              NEXT<span className="text-primary">/</span>GEN
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline truncate max-w-[150px] md:max-w-none">{user.email}</span>
              <NotificationBell testIdPrefix="client" />
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-xs sm:text-sm">
                <LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black uppercase mb-2">Welcome, {user.name?.split(' ')[0]}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Manage your services, invoices, and support tickets</p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
          <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              <span className="text-2xl sm:text-3xl font-black">{stats.invoices}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Total Invoices</p>
          </Card>

          <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
              <span className="text-2xl sm:text-3xl font-black">{stats.pendingInvoices}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Pending</p>
          </Card>

          <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <Ticket className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              <span className="text-2xl sm:text-3xl font-black">{stats.tickets}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Total Tickets</p>
          </Card>

          <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <Ticket className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
              <span className="text-2xl sm:text-3xl font-black">{stats.openTickets}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Open</p>
          </Card>
        </div>

        {/* Service Usage Metrics */}
        {dashboardMetrics && dashboardMetrics.active_services > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold uppercase mb-4 sm:mb-6">Service Performance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
              <Card className="p-4 sm:p-6 border-border">
                <div className="flex items-center gap-3 sm:gap-4">
                  <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                  <div>
                    <div className="text-2xl sm:text-3xl font-black">{dashboardMetrics.overall_uptime}%</div>
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Overall Uptime</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6 border-border">
                <div className="flex items-center gap-3 sm:gap-4">
                  <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                  <div>
                    <div className="text-2xl sm:text-3xl font-black">{dashboardMetrics.total_requests.toLocaleString()}</div>
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Total Requests</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-6 border-border">
                <div className="flex items-center gap-3 sm:gap-4">
                  <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                  <div>
                    <div className="text-2xl sm:text-3xl font-black">{dashboardMetrics.active_services}</div>
                    <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider">Active Services</p>
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}>
          <h2 className="text-xl sm:text-2xl font-bold uppercase mb-4 sm:mb-6">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6">
            <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-all cursor-pointer group" onClick={() => navigate('/credentials')}>
              <Key className="w-8 h-8 sm:w-12 sm:h-12 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2">Credentials</h3>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Secure password storage</p>
            </Card>

            <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-all cursor-pointer group" onClick={() => navigate('/subscriptions')}>
              <Settings className="w-8 h-8 sm:w-12 sm:h-12 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2">Subscriptions</h3>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Manage subscriptions</p>
            </Card>

            <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-all cursor-pointer group" onClick={() => navigate('/services')}>
              <ShoppingCart className="w-8 h-8 sm:w-12 sm:h-12 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2">Services</h3>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Subscribe to services</p>
            </Card>

            <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-all cursor-pointer group" onClick={() => navigate('/invoices')}>
              <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2">Invoices</h3>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Manage payments</p>
            </Card>

            <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-all cursor-pointer group" onClick={() => navigate('/tickets')}>
              <Ticket className="w-8 h-8 sm:w-12 sm:h-12 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2">Tickets</h3>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Support requests</p>
            </Card>

            {user.role === 'admin' && (
              <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-all cursor-pointer group col-span-2 sm:col-span-1" onClick={() => navigate('/admin')}>
                <Shield className="w-8 h-8 sm:w-12 sm:h-12 text-primary mb-2 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2">Admin Panel</h3>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">System management</p>
              </Card>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
